import React, { useState, useEffect } from "react";
import {
  Button,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Dialog,
  Typography,
  List,
  ListItem,
  ListItemText,
  Divider,
  ListItemIcon, Grid, Card, CardContent, Link
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { useDealer } from "context/dealer/dealerContext";
import { useNavigate, useParams } from "react-router-dom";
import ConditionRating from "components/ConditionRating";
import { useUI } from "context/ui/uiContext";
import { DialogContainer, DialogInput } from "./styles";
import SideBar from "../../../../components/dealer/Inspection/SideBar";
import {AccountBoxRounded, LightbulbRounded} from "@mui/icons-material";

const DealerInspection = () => {
  const { inspection,
    fetchCRMaster,
    fetchVehicleDetails,
    submitDealerConditionReport,
    submitPOADetails,
    addImage,
    images,
    charges,
    inspectionID,
    removeImage,
    EWTChargesDecision} = useDealer();
  const [vehicle, setVehicle] = useState(false);
  const navigate = useNavigate();
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [openChargesDialog, setOpenChargesDialog] = useState(false);
  const [openNameEmailDialog, setOpenNameEmailDialog] = useState(false);
  const { showAlert, showLoadingSpinner, hideLoadingSpinner } = useUI();
  const [poaSigner1, setPoaSigner1] = useState("");
  const [poaSigner1Email, setPoaSigner1Email] = useState("");
  const [poaSigner2, setPoaSigner2] = useState("");
  const [poaSigner2Email, setPoaSigner2Email] = useState("");
  const [inspectionToBeSubmitted, setInspectionToBeSubmitted] = useState(null);
  const [openDeclineDialog, setOpenDeclineDialog] = useState(false);
  const [openCompleteDialog, setOpenCompleteDialog] = useState(false);
  const [loading, setLoading] = useState(false);

  const { vehicleID } = useParams();

  useEffect(() => {
    if (vehicle?.poaSigner1) setPoaSigner1(vehicle?.poaSigner1);
    if (vehicle?.poaSigner1Email) setPoaSigner1Email(vehicle?.poaSigner1Email);
    if (vehicle?.poaSigner2) setPoaSigner2(vehicle?.poaSigner2);
    if (vehicle?.poaSigner2Email) setPoaSigner2Email(vehicle?.poaSigner2Email);
  }, [vehicle]);

  useEffect(() => {
    if (vehicleID) loadInspection(vehicleID);
  }, [vehicleID]);

  const loadInspection = async (vehicleID) => {
    try {
      showLoadingSpinner();
      const v = await fetchVehicleDetails({ vehicleID });
      await fetchCRMaster({ vehicleID });
      setVehicle(v);
      hideLoadingSpinner();
    } catch (error) {
      hideLoadingSpinner();
      showAlert(error?.message, "error");
    }
  };

  const submitInspection = async (conditionResult, mileage) => {
    setInspectionToBeSubmitted({ conditionResult, mileage });
    setOpenConfirmDialog(true);
  };

  const handleDecline = () => {
    setOpenNameEmailDialog(false);
    setOpenDeclineDialog(true);
  };

  const handleDeclineConfirm = async () => {
    await EWTChargesDecision({
      ewtChargesAccepted: false,
      ewtCharges: charges?.totalActualChargeIncludingPickup,
      poaSigner: poaSigner1,
      customerName: vehicle?.customerName,
      inspectionID
    });
    setOpenDeclineDialog(false);
    setOpenCompleteDialog(true);
  };

  const handleConfirmSubmitInspection = async () => {
    setLoading(true);
    showLoadingSpinner();
    const { conditionResult, mileage } = inspectionToBeSubmitted;
    const {success, message} = await submitDealerConditionReport({ conditionResult, mileage, vehicleID });
    hideLoadingSpinner();

    if (success) {
      setOpenConfirmDialog(false);
      setOpenChargesDialog(true);
      setLoading(false);
    } else {
      showAlert(message, "error");
      setLoading(false);
    }
  };

  const handlePOASubmit = async () => {
    try {
      if (!poaSigner1Email) {
        return;
      }
      setLoading(true);
      await submitPOADetails(vehicleID, {
        poaSigner1,
        poaSigner1Email,
        poaSigner2,
        poaSigner2Email
      });

      await EWTChargesDecision({
        ewtChargesAccepted: true,
        ewtCharges: charges?.totalActualChargeIncludingPickup,
        customerName: vehicle?.customerName || poaSigner1,
        inspectionID,
        comment: "none"
      });

      setOpenNameEmailDialog(false);
      setLoading(false);
      navigate("/dealer/dashboard");
    } catch (error) {
      setLoading(false);
      showAlert("Failed to submit POA details.", "error");
    }
  };

  if (!inspection || !vehicle) {
    return null;
  }

  return (
    <>
      <ConditionRating
        vehicle={vehicle}
        removeImage={removeImage}
        addImage={addImage}
        images={images}
        inspection={inspection}
        submitConditionReport={submitInspection}
        isDealer={true}>
        <Grid item xs={12} md={4} sx={{ pr: 2 }}>
          <SideBar
            unit={{
              year: vehicle?.year || "N/A",
              make: vehicle?.brand || "N/A",
              model: vehicle?.model || "N/A",
              vin: vehicle?.vin || "N/A",
              imageUrl: vehicle?.image,
            }}
          />

          {/* Help Section */}
          <Grid item xs={12} sx={{ mt: 2 }}>
            <Card elevation={3}>
              <CardContent>
                <Grid container alignItems="center" spacing={1}>
                  <Grid item>
                    <AccountBoxRounded fontSize="medium" />
                  </Grid>
                  <Grid item>
                    <Typography variant="h6" gutterBottom>
                      Help
                    </Typography>
                  </Grid>
                </Grid>
                <Typography variant="body1" gutterBottom>
                  For questions regarding a customer’s HDFS loan, end of loan options, or the H-D Flex Financing program, contact our <strong>Flex Financing team at <Link href="tel:800-717-3450" underline="hover">800-717-3450</Link></strong>.
                </Typography>
                <Typography variant="body1" gutterBottom>
                  For questions regarding the title or title process, contact the Titles Department at <Link href="tel:866-433-7562" underline="hover">866-433-7562</Link>.
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </ConditionRating>

      {/* Confirmation Dialog */}
      <Dialog
        open={openConfirmDialog}
        onClose={(event, reason) => {
          if (reason !== "backdropClick") {
            setOpenConfirmDialog(false);
          }
        }}
        disableEscapeKeyDown
      >
        <DialogTitle>Confirm Submission</DialogTitle>
        <DialogContent>
          <DialogContentText>
            The information you have submitted cannot be changed. Are you sure you want to proceed?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenConfirmDialog(false)} color="secondary">Cancel</Button>
          <Button disabled={loading} onClick={handleConfirmSubmitInspection} color="primary">Submit</Button>
        </DialogActions>
      </Dialog>

      {/* Final Amount Charges Dialog */}
      <Dialog
        open={openChargesDialog}
        onClose={(event, reason) => {
          if (reason !== "backdropClick") {
            setOpenChargesDialog(false);
          }
        }}
        disableEscapeKeyDown
        maxWidth="sm" fullWidth>
        <DialogTitle>Turn-In Charge</DialogTitle>
        <DialogContent>
          <Typography variant="h6" gutterBottom>Final Amount</Typography>
          <Typography variant="h4" color="primary" fontWeight="bold">
            ${charges?.totalActualChargeIncludingPickup?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </Typography>
          <Typography variant="subtitle1" gutterBottom>Breakdown of Charges</Typography>
          <List disablePadding>
            {charges?.selectedItemCharges?.map((chargeItem, index) => (
              <React.Fragment key={index}>
                {chargeItem.options.map((optionItem, optionIndex) => (
                  <ListItem key={optionIndex}>
                    <ListItemIcon><CheckCircleIcon color="error" /></ListItemIcon>
                    <ListItemText primary={optionItem.option} secondary={`$${optionItem.charge?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`} />
                  </ListItem>
                ))}

                <Divider />
              </React.Fragment>
            ))}

            {charges?.pickupCharge ? <React.Fragment><ListItem>
              <ListItemIcon><CheckCircleIcon color="error" /></ListItemIcon>
              <ListItemText primary="Pickup Charge" secondary={`$${charges?.pickupCharge?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`} />
            </ListItem><Divider /></React.Fragment> : null}

            {charges?.dispositionCharge ? <React.Fragment><ListItem>
              <ListItemIcon><CheckCircleIcon color="error" /></ListItemIcon>
              <ListItemText primary="Disposition Charge" secondary={`$${charges?.dispositionCharge?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`} />
            </ListItem><Divider /></React.Fragment> : null}

            {charges?.estimatedMileageCharge ? <React.Fragment><ListItem>
              <ListItemIcon><CheckCircleIcon color="error" /></ListItemIcon>
              <ListItemText primary="Mileage Charge" secondary={`$${charges?.estimatedMileageCharge?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`} />
            </ListItem><Divider /></React.Fragment> : null}
          </List>
          <Typography variant="body2" gutterBottom>
            This is the total amount owed based on the completed inspection report.  All charges will be applied to the customer’s HDFS account upon final submission.  The customer will receive their Flex Financing End of Loan statement by mail approximately 14 days after return detailing any charges incurred.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenChargesDialog(false) || setOpenNameEmailDialog(true)} color="primary" variant="contained">
            Accept
          </Button>
          <Button onClick={handleDecline} color="error" autoFocus>Decline</Button>
        </DialogActions>
      </Dialog>

      {/* POA Confirmation Dialog */}
      <Dialog
        open={openNameEmailDialog}
        onClose={(event, reason) => {
          if (reason !== "backdropClick") {
            setOpenNameEmailDialog(false);
          }
        }}
        disableEscapeKeyDown
      >
        <DialogTitle>Confirm Customer Details</DialogTitle>
        <DialogContent>
          <DialogContentText>
            A signed Power of Attorney is required to complete the title process.  Please confirm the customer&apos;s email address before proceeding to receive and sign an electronic POA to complete the turn-in process.  Turn-in cannot be completed without successful completion of the POA.  If there are questions, please contact a Titles Specialist at <Link href="tel:866-433-7562" underline="hover">866-433-7562</Link>.
          </DialogContentText>
          <DialogInput disabled margin="dense" size="small" label="Customer Name" type="text" fullWidth value={poaSigner1} onChange={(e) => setPoaSigner1(e.target.value)} />
          <DialogInput margin="dense" size="small" label="Customer Email" type="email" fullWidth value={poaSigner1Email} onChange={(e) => setPoaSigner1Email(e.target.value)} />
          {vehicle?.poaSigner2 && (
            <>
              <DialogInput disabled margin="dense" size="small" label="Customer Name" type="text" fullWidth value={poaSigner2} onChange={(e) => setPoaSigner2(e.target.value)} />
              <DialogInput margin="dense" size="small" label="Customer Email" type="email" fullWidth value={poaSigner2Email} onChange={(e) => setPoaSigner2Email(e.target.value)} />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button disabled={loading} onClick={handlePOASubmit} color="primary" autoFocus>Continue</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openDeclineDialog}
        onClose={(event, reason) => {
          if (reason !== "backdropClick") {
            setOpenDeclineDialog(false);
          }
        }}
        disableEscapeKeyDown
      >
        <DialogTitle>Confirm Decline</DialogTitle>
        <DialogContent>
          <DialogContentText>
            The turn-in process will be ended. Are you sure you want to decline?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeclineDialog(false)} color="primary">
            Cancel
          </Button>
          <Button onClick={handleDeclineConfirm} color="error">
            Decline
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openCompleteDialog}
        onClose={(event, reason) => {
          if (reason !== "backdropClick") {
            setOpenCompleteDialog(false);
          }
        }}
        disableEscapeKeyDown
      >
        <DialogTitle>Completed</DialogTitle>
        <DialogContent>
          <DialogContentText>
            The inspection has been complete and the return may be finalized if the customer has completed the electronic POA.  Ensure all personal property has been removed.  You will be notified when the bike is available for purchase to your dealership. Questions? Contact us at 800-717-3450.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setOpenCompleteDialog(false);
            navigate("/dealer/dashboard");
          }}
          color="primary">
            Continue
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default DealerInspection;
